import cn from "classnames";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { useState } from "react";
import { Pagination, SearchInput } from "ui";

import { useGetTransferSummary } from "../../apis";
import { CancelTransferModal, TransfersTable } from "../../components";
import {
  ETransferView,
  TRANSFER_STATUS_TABS,
  TRANSFER_VIEW_TABS,
} from "../../constants";
import { useTransfersList } from "../../hooks";
import { Transfer } from "../../transfers.types";
import { BoxMovements } from "../BoxMovements";

import styles from "./Transfers.module.scss";

const Transfers = () => {
  const [view, setView] = useState<ETransferView>(ETransferView.Transfers);

  const {
    transfersList,
    isLoading,
    page,
    totalItems,
    sorting,
    status,
    searchValue,
    setSearchValue,
    setStatus,
    setSorting,
    setPage,
    refetchTransfers,
  } = useTransfersList();

  const { summary } = useGetTransferSummary();

  const tabCount = (
    tabStatus?: (typeof TRANSFER_STATUS_TABS)[number]["status"],
  ) =>
    tabStatus
      ? (summary?.statusCounts?.[tabStatus] ?? 0)
      : (summary?.total ?? 0);

  const [cancelTarget, setCancelTarget] = useState<Transfer | null>(null);
  const {
    value: isCancelOpen,
    setTrue: openCancel,
    setFalse: closeCancel,
  } = useBoolean(false);

  const handleCancelClick = (transfer: Transfer) => {
    setCancelTarget(transfer);
    openCancel();
  };

  const handleCancelClose = () => {
    closeCancel();
    setCancelTarget(null);
  };

  return (
    <div className={styles.transfers}>
      <div className={styles.transfers_viewTabs}>
        {TRANSFER_VIEW_TABS.map((tab) => (
          <button
            key={tab.view}
            type="button"
            className={cn(styles.transfers_viewTab, {
              [styles.transfers_viewTab__active]: view === tab.view,
            })}
            onClick={() => setView(tab.view)}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>

      {view === ETransferView.BoxMovements ? (
        <BoxMovements />
      ) : (
        <>
          <div className={styles.transfers_summary}>
            <span className={styles.transfers_summary_count}>
              {t("{{count}} active transfers", {
                count: summary?.activeCount ?? 0,
              })}
            </span>
            <span className={styles.transfers_summary_items}>
              {t("{{count}} items in transit", {
                count: summary?.totalItems ?? 0,
              })}
            </span>
          </div>

          <div className={styles.transfers_controls}>
            <div className={styles.transfers_tabs}>
              {TRANSFER_STATUS_TABS.map((tab) => (
                <button
                  key={tab.label}
                  type="button"
                  className={cn(styles.transfers_tab, {
                    [styles.transfers_tab__active]: status === tab.status,
                  })}
                  onClick={() => setStatus(tab.status)}
                >
                  {t(tab.label)}
                  <span className={styles.transfers_tab_count}>
                    {tabCount(tab.status)}
                  </span>
                </button>
              ))}
            </div>

            <div className={styles.transfers_search}>
              <SearchInput
                value={searchValue}
                onChange={setSearchValue}
                placeholder={`${t("Search")}...`}
              />
            </div>
          </div>

          <div className={styles.transfers_tableContainer}>
            <TransfersTable
              transfers={transfersList ?? []}
              isLoading={isLoading}
              sorting={sorting}
              setSorting={setSorting}
              onCancelTransfer={handleCancelClick}
            />
          </div>

          <Pagination
            currentPage={page}
            onChange={setPage}
            perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
            totalItems={totalItems}
          />

          <CancelTransferModal
            isOpen={isCancelOpen}
            transfer={cancelTarget}
            onClose={handleCancelClose}
            refetch={refetchTransfers}
          />
        </>
      )}
    </div>
  );
};

export default Transfers;
